package app.footyguide.web.data

import app.footyguide.domain.model.GameRepository
import app.footyguide.domain.model.BroadcastStatus
import app.footyguide.domain.model.Game
import app.footyguide.web.page.ensureAlphanumericMatchId
import app.footyguide.domain.model.MatchStatus
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json

/**
 * Implementação do GameRepository para Kotlin/JS.
 *
 * Usa Firebase JS SDK (FirestoreSdk) com suporte a persistência offline
 * e named databases (dev/prod).
 * Estrutura: /games/{date}/matches/{matchId}
 */
class WebGameRepository : GameRepository {

    private val json = Json { ignoreUnknownKeys = true }
    private val recentGamesById = mutableMapOf<String, Game>()

    private fun indexGames(games: List<Game>) {
        games.forEach { g ->
            recentGamesById[g.id] = g
            recentGamesById[app.footyguide.web.page.ensureAlphanumericMatchId(g.id)] = g
            recentGamesById[app.footyguide.web.page.ensureAlphanumericMatchId("${g.homeTeamId}_vs_${g.awayTeamId}")] = g
            recentGamesById["${g.homeTeamId}-${g.awayTeamId}"] = g
            recentGamesById["${g.homeTeamId}_vs_${g.awayTeamId}"] = g
        }
    }

    override suspend fun getGamesByDate(date: String): List<Game> {
        val cacheKey = cacheKeyForDate(date)
        val parentDoc = try {
            FirestoreSdk.document("games/$date")
        } catch (_: Exception) {
            null
        }
        val serverLastUpdate = parentDoc?.string("updatedAt") ?: parentDoc?.string("lastUpdate")

        if (!serverLastUpdate.isNullOrBlank()) {
            val cached = LocalCache.getIfVersionMatches(cacheKey, serverLastUpdate)
            if (cached != null) {
                val parsed = deduplicateGames(parseGamesFromJson(cached))
                indexGames(parsed)
                return parsed
            }
        }

        val fresh = deduplicateGames(getMatchesForDate(date))
        if (fresh.isNotEmpty()) {
            val effectiveVersion = serverLastUpdate
                ?: fresh.mapNotNull { it.lastUpdate }.maxOrNull()
            indexGames(fresh)
            LocalCache.set(cacheKey, serializeGames(fresh), lastUpdate = effectiveVersion)
            return fresh
        }

        // Offline fallback only when browser is offline
        val isOnline = js("(typeof navigator === 'undefined' || navigator.onLine !== false)") as Boolean
        if (!isOnline) {
            val offlineCached = LocalCache.get(cacheKey, GAME_CACHE_TTL_MS)
            if (offlineCached != null) {
                val parsed = deduplicateGames(parseGamesFromJson(offlineCached))
                indexGames(parsed)
                return parsed
            }
        } else {
            LocalCache.remove(cacheKey)
        }
        return emptyList()
    }

    suspend fun refreshGamesByDate(date: String): List<Game> {
        val cacheKey = cacheKeyForDate(date)
        val cached = LocalCache.get(cacheKey, GAME_CACHE_TTL_MS)
            ?.let(::parseGamesFromJson)
            ?.let(::deduplicateGames)
            .orEmpty()
        val fresh = deduplicateGames(getMatchesForDate(date))
        if (fresh.isEmpty()) {
            indexGames(cached)
            return cached
        }
        val freshIds = fresh.mapTo(mutableSetOf()) { it.id }
        val merged = (cached.filterNot { it.id in freshIds } + fresh).sortedBy { it.kickoff }
        indexGames(merged)
        LocalCache.set(cacheKey, serializeGames(merged))
        return merged
    }

    suspend fun refreshGameById(gameId: String, date: String): Game? {
        val games = refreshGamesByDate(date)
        val cleanId = gameId.substringAfterLast("/")
        val slug = parseGameSlug(gameId)
        return games.firstOrNull { game ->
            game.id == cleanId || (slug != null && game.homeTeamId == slug.homeTeamSlug && game.awayTeamId == slug.awayTeamSlug)
        }
    }

    fun applyLiveScores(games: List<Game>, scores: List<LiveMatchScore>): List<Game> {
        if (games.isEmpty() || scores.isEmpty()) return games
        val scoresById = scores.associateBy { ensureAlphanumericMatchId(it.matchId) }
        val updatedScores = games.mapNotNull { game ->
            scoresById[game.id]?.let { score ->
                game.copy(homeScore = score.homeScore, awayScore = score.awayScore)
            }
        }.associateBy { it.id }
        if (updatedScores.isEmpty()) return games

        val updated = games.map { updatedScores[it.id] ?: it }
        indexGames(updated)
        updated.groupBy { it.date }.forEach dateLoop@{ (date, dateGames) ->
            val cached = LocalCache.get(cacheKeyForDate(date), GAME_CACHE_TTL_MS)
                ?.let(::parseGamesFromJson)
                ?: return@dateLoop
            val cachedUpdates = dateGames.associateBy { it.id }
            LocalCache.set(
                cacheKeyForDate(date),
                serializeGames(cached.map { cachedUpdates[it.id] ?: it }),
            )
        }
        return updated
    }

    override suspend fun getGamesByCompetition(
        competitionId: String,
        fromDate: String,
        toDate: String,
    ): List<Game> {
        return try {
            val query = StructuredQuery("matches", allDescendants = true)
                .where("competitionId", "==", competitionId)
                .where("date", ">=", fromDate)
                .where("date", "<=", toDate)
                .orderBy("date", "asc")
                .orderBy("kickoff", "asc")

            val deduped = deduplicateGames(FirestoreSdk.runQuery(query).mapNotNull { docToGame(it) })
            indexGames(deduped)
            deduped
        } catch (e: Exception) {
            console.error("[WebGameRepository] Erro em getGamesByCompetition: ${e.message}")
            emptyList()
        }
    }

    override suspend fun getGamesByTeam(
        teamId: String,
        fromDate: String,
        toDate: String,
    ): List<Game> {
        val canonicalId = app.footyguide.core.theme.ClubCatalog.bySlug(teamId)?.slug ?: teamId
        val candidateIds = if (canonicalId == "remo") listOf("remo", "clube-do-remo") else listOf(canonicalId)
        val combined = mutableListOf<Game>()
        for (candidate in candidateIds) {
            val rows = try {
                val query = StructuredQuery("matches", allDescendants = true)
                    .where("teamIds", "array-contains", candidate)
                    .where("date", ">=", fromDate)
                    .where("date", "<=", toDate)
                    .orderBy("date", "asc")
                    .orderBy("kickoff", "asc")

                FirestoreSdk.runQuery(query).mapNotNull { docToGame(it) }
            } catch (e: Exception) {
                console.error("[WebGameRepository] Erro em getGamesByTeam($candidate): ${e.message}")
                emptyList()
            }
            combined.addAll(rows)
        }
        val deduped = deduplicateGames(combined)
        indexGames(deduped)
        return deduped
    }

    override suspend fun getGameById(gameId: String): Game? {
        val cleanId = gameId.substringAfterLast("/")
        recentGamesById[cleanId]?.let { return it }

        // O(1) lookup via matchLookup/{matchId}
        if (cleanId.isNotBlank() && !cleanId.contains("-") && !cleanId.contains("_vs_")) {
            try {
                val lookupDoc = FirestoreSdk.document("matchLookup/$cleanId")
                val lookupDate = lookupDoc?.string("date")
                if (!lookupDate.isNullOrBlank()) {
                    val matchDoc = FirestoreSdk.document("games/$lookupDate/matches/$cleanId")
                    val resolved = if (matchDoc != null) docToGame(matchDoc) else null
                    if (resolved != null) {
                        indexGames(listOf(resolved))
                        return resolved
                    }
                }
            } catch (_: Exception) {
            }
        }

        // Check today's games first (from local cache or Firestore)
        val today = js("new Date().toLocaleDateString('sv-SE')") as String
        getGamesByDate(today)
        recentGamesById[cleanId]?.let { return it }

        // Indexed collectionGroup query on teamIds (para slugs home-away ou home_vs_away)
        val slugPart = gameId.substringBefore("/")
        val pair = when {
            cleanId.contains("_vs_") -> cleanId.split("_vs_", limit = 2)
            slugPart.contains("-") -> slugPart.split("-", limit = 2)
            cleanId.contains("-") -> cleanId.split("-", limit = 2)
            else -> emptyList()
        }
        if (pair.size == 2) {
            val homeCandidate = canonicalTeamId(pair[0])
            val awayCandidate = canonicalTeamId(pair[1])
            recentGamesById["${homeCandidate}-${awayCandidate}"]?.let { return it }
            try {
                val query = StructuredQuery("matches", allDescendants = true)
                    .where("teamIds", "array-contains", homeCandidate)
                    .where("date", ">=", "2026-01-01")
                    .orderBy("date", "asc")
                    .orderBy("kickoff", "asc")
                    .limit(200)
                val docs = FirestoreSdk.runQuery(query).mapNotNull { docToGame(it) }
                val exactMatches = docs.filter { it.id == cleanId }
                val matched = exactMatches.firstOrNull { it.matchStatus != MatchStatus.FINISHED }
                    ?: exactMatches.lastOrNull()
                    ?: docs.firstOrNull {
                        it.homeTeamId == homeCandidate &&
                            it.awayTeamId == awayCandidate &&
                            it.matchStatus != MatchStatus.FINISHED
                    }
                    ?: docs.lastOrNull { it.homeTeamId == homeCandidate && it.awayTeamId == awayCandidate }
                if (matched != null) {
                    indexGames(listOf(matched))
                    return matched
                }
            } catch (e: Exception) {
                console.warn("[WebGameRepository] Falha ao buscar por teamIds: ${e.message}")
            }
        }

        val parsed = parseGameSlug(gameId)
        if (parsed != null) {
            val found = getGamesByDate(parsed.date).firstOrNull {
                it.homeTeamId == parsed.homeTeamSlug && it.awayTeamId == parsed.awayTeamSlug
            }
            if (found != null) return found
        }
        return null
    }

    override suspend fun getLiveGames(): List<Game> {
        val today = js("new Date().toLocaleDateString('sv-SE')") as String
        return getGamesByDate(today).filter { game ->
            game.matchStatus in listOf(
                MatchStatus.FIRST_HALF, MatchStatus.HALFTIME,
                MatchStatus.SECOND_HALF, MatchStatus.EXTRA_TIME, MatchStatus.PENALTIES,
            )
        }
    }

    override suspend fun findNextGameDate(afterDate: String): String? {
        return try {
            val query = StructuredQuery("matches", allDescendants = true)
                .where("date", ">", afterDate)
                .orderBy("date", "asc")
                .orderBy("kickoff", "asc")
                .limit(1)
            val docs = FirestoreSdk.runQuery(query)
            docs.firstOrNull()?.string("date")
        } catch (e: Exception) {
            console.error("[WebGameRepository] Erro findNextGameDate: ${e.message}")
            null
        }
    }

    override suspend fun findNextGameByTeam(teamId: String, afterDate: String): Game? {
        val canonicalId = canonicalTeamId(teamId)
        return try {
            val query = StructuredQuery("matches", allDescendants = true)
                .where("teamIds", "array-contains", canonicalId)
                .where("date", ">=", afterDate)
                .orderBy("date", "asc")
                .orderBy("kickoff", "asc")
                .limit(1)
            val docs = FirestoreSdk.runQuery(query)
            docs.firstOrNull()?.let { docToGame(it) }
        } catch (e: Exception) {
            console.error("[WebGameRepository] Erro findNextGameByTeam: ${e.message}")
            null
        }
    }

    /** Fetch matches from the nested /games/{date}/matches collection. */
    private suspend fun getMatchesForDate(date: String): List<Game> {
        return try {
            val path = "games/$date/matches"
            val docs = FirestoreSdk.collection(path)

            docs.mapNotNull { docToGame(it) }.sortedBy { it.kickoff }
        } catch (e: Exception) {
            console.error("[WebGameRepository] Erro ao buscar jogos para $date: ${e.message}")
            emptyList()
        }
    }

    private fun cacheKeyForDate(date: String): String = "fg-cache-games-v25-$date"

    private fun canonicalTeamId(raw: String): String =
        app.footyguide.core.theme.ClubCatalog.bySlug(raw)?.slug ?: raw.trim().lowercase()

    private fun deduplicateGames(games: List<Game>): List<Game> =
        app.footyguide.web.components.club.deduplicateTeamSeasonGames(games)

    private fun serializeGames(games: List<Game>): String =
        json.encodeToString(ListSerializer(Game.serializer()), games)

    private fun parseGamesFromJson(jsonString: String): List<Game> {
        return try {
            json.decodeFromString(ListSerializer(Game.serializer()), jsonString)
        } catch (_: Exception) {
            emptyList()
        }
    }

    private fun parseGameSlug(gameId: String): ParsedGameId? {
        val parts = gameId.split("/")
        if (parts.size != 2) return null
        val date = parts[0]
        if (!date.matches(Regex("^\\d{4}-\\d{2}-\\d{2}$"))) return null
        val teamParts = parts[1].split("_x_")
        if (teamParts.size != 2) return null
        return ParsedGameId(
            date = date,
            homeTeamSlug = teamParts[0],
            awayTeamSlug = teamParts[1],
        )
    }

    // ── Document → Game parsing ──────────────────────────────

    internal fun docToGame(doc: SdkDocument): Game? {
        val competitionId = doc.string("competitionId") ?: return null
        val rawHomeId = doc.string("homeTeamId") ?: return null
        val rawAwayId = doc.string("awayTeamId") ?: return null
        val homeTeamId = canonicalTeamId(rawHomeId)
        val awayTeamId = canonicalTeamId(rawAwayId)
        val kickoff = doc.string("kickoff") ?: return null
        val date = doc.string("date") ?: kickoff.take(10)
        val rawId = doc.string("matchId") ?: doc.string("id") ?: doc.id

        return Game(
            id = app.footyguide.web.page.ensureAlphanumericMatchId(rawId),
            competitionId = competitionId,
            matchday = doc.int("matchday"),
            stage = doc.string("stage"),
            group = doc.string("group"),
            homeTeamId = homeTeamId,
            awayTeamId = awayTeamId,
            teamIds = (doc.stringList("teamIds").map { canonicalTeamId(it) } + listOf(homeTeamId, awayTeamId)).distinct(),
            kickoff = kickoff,
            date = date,
            channelsByCountry = doc.stringListMap("channelsByCountry"),
            broadcastStatusByCountry = parseBroadcastStatus(doc.stringMap("broadcastStatusByCountry")),
            matchStatus = parseMatchStatus(doc.string("matchStatus")),
            homeScore = doc.nestedMap("live")?.nestedMap("score")?.int("home") ?: doc.int("homeScore"),
            awayScore = doc.nestedMap("live")?.nestedMap("score")?.int("away") ?: doc.int("awayScore"),
            stadium = doc.string("stadium"),
            stadiumCity = doc.string("stadiumCity"),
            stadiumCapacity = doc.int("stadiumCapacity"),
            stadiumImage = doc.string("stadiumImage"),
            lastUpdate = doc.string("lastUpdate") ?: doc.string("updatedAt"),
        )
    }

    private fun parseBroadcastStatus(raw: Map<String, String>): Map<String, BroadcastStatus> =
        raw.mapValues { (_, v) ->
            when (v) {
                "confirmed" -> BroadcastStatus.CONFIRMED
                "unavailable" -> BroadcastStatus.UNAVAILABLE
                else -> BroadcastStatus.PENDING
            }
        }

    private fun parseMatchStatus(value: String?): MatchStatus = when (value) {
        "NS", "NOT_STARTED" -> MatchStatus.NOT_STARTED
        "1H", "FIRST_HALF" -> MatchStatus.FIRST_HALF
        "HT", "HALFTIME" -> MatchStatus.HALFTIME
        "2H", "SECOND_HALF" -> MatchStatus.SECOND_HALF
        "FT", "FINISHED" -> MatchStatus.FINISHED
        "ET", "EXTRA_TIME" -> MatchStatus.EXTRA_TIME
        "P", "PENALTIES" -> MatchStatus.PENALTIES
        "PST", "POSTPONED" -> MatchStatus.POSTPONED
        "CANC", "CANCELLED" -> MatchStatus.CANCELLED
        else -> MatchStatus.NOT_STARTED
    }

    companion object {
        private const val GAME_CACHE_TTL_MS = 3 * 60 * 1000L
    }
}

data class LiveMatchScore(val matchId: String, val homeScore: Int, val awayScore: Int)

private data class ParsedGameId(
    val date: String,
    val homeTeamSlug: String,
    val awayTeamSlug: String,
)
